import { useState } from 'react'
import { DashboardConfig } from '../types/dashboard'
import './DashboardCard.css'

interface DashboardCardProps {
  dashboard: DashboardConfig
  hasAccess: boolean
  missingRoles?: string[]
  isFavorite: boolean
  isRecent?: boolean
  onNavigate: (dashboardId: string) => void
  onToggleFavorite: (dashboardId: string) => void
  onOpenInNewTab?: (dashboardId: string) => void
}

const DashboardCard = ({
  dashboard,
  hasAccess,
  isFavorite,
  isRecent = false,
  onNavigate,
  onToggleFavorite,
  onOpenInNewTab
}: DashboardCardProps) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleCardClick = () => {
    if (hasAccess) {
      onNavigate(dashboard.id)
    }
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleFavorite(dashboard.id)
  }

  const handleNewTabClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasAccess && onOpenInNewTab) {
      onOpenInNewTab(dashboard.id)
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    if (hasAccess && onOpenInNewTab) {
      onOpenInNewTab(dashboard.id)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return '🟢'
      case 'offline': return '🔴'
      case 'maintenance': return '🟡'
      case 'development': return '🔵'
      default: return '⚪'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online': return 'Online'
      case 'offline': return 'Offline'
      case 'maintenance': return 'Maintenance'
      case 'development': return 'Development'
      default: return 'Unknown'
    }
  }

  const formatLastUpdated = (dateString?: string) => {
    if (!dateString) return 'No data'
    
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
      
      if (diffHours < 1) return 'Just updated'
      if (diffHours < 24) return `${diffHours}h ago`
      
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 7) return `${diffDays}d ago`
      
      return date.toLocaleDateString()
    } catch {
      return 'Invalid date'
    }
  }

  return (
    <div
      className={`dashboard-card ${hasAccess ? 'accessible' : 'restricted'} ${dashboard.status}`}
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={hasAccess ? 0 : -1}
      aria-label={`${dashboard.name} dashboard ${hasAccess ? '(accessible)' : '(restricted)'}`}
    >
      {/* Card Header */}
      <div className="card-header">
        <div className="header-top">
          <div className="status-indicator">
            <span className="status-icon">{getStatusIcon(dashboard.status)}</span>
            <span className="status-text">{getStatusLabel(dashboard.status)}</span>
          </div>
          
          <div className="card-actions">
            {isRecent && <span className="recent-badge" title="Recently accessed">⏱️</span>}
            <button
              className={`favorite-btn ${isFavorite ? 'active' : ''}`}
              onClick={handleFavoriteClick}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorite ? '★' : '☆'}
            </button>
            {hasAccess && onOpenInNewTab && (
              <button
                className="new-tab-btn"
                onClick={handleNewTabClick}
                title="Open in new tab"
                aria-label="Open dashboard in new tab"
              >
                ↗️
              </button>
            )}
          </div>
        </div>

        {/* Category Info (client info removed for security) */}
        {dashboard.category && (
          <div className="category-info">
            <span className="category-badge">{dashboard.category}</span>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="card-content">
        <h3 className="dashboard-title">{dashboard.name}</h3>
        <p className="dashboard-description">{dashboard.description}</p>

        {/* Key Metrics Preview */}
        {dashboard.keyMetrics && dashboard.keyMetrics.length > 0 && (
          <div className="key-metrics">
            <span className="metrics-label">Key Metrics:</span>
            <div className="metrics-list">
              {dashboard.keyMetrics.slice(0, 3).map((metric, index) => (
                <span key={index} className="metric-tag">{metric}</span>
              ))}
              {dashboard.keyMetrics.length > 3 && (
                <span className="metric-more">+{dashboard.keyMetrics.length - 3} more</span>
              )}
            </div>
          </div>
        )}

        {/* Tags */}
        {dashboard.tags && dashboard.tags.length > 0 && (
          <div className="dashboard-tags">
            {dashboard.tags.slice(0, 4).map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
            {dashboard.tags.length > 4 && (
              <span className="tag-more">+{dashboard.tags.length - 4}</span>
            )}
          </div>
        )}
      </div>

      {/* Card Footer */}
      <div className="card-footer">
        <div className="last-updated">
          <span className="update-label">Updated:</span>
          <span className="update-time">{formatLastUpdated(dashboard.lastUpdated)}</span>
        </div>
      </div>

      {/* Hover Effect Overlay */}
      {isHovered && hasAccess && (
        <div className="hover-overlay">
          <div className="access-prompt">
            <span className="access-icon">🚀</span>
            <span className="access-text">Click to access dashboard</span>
          </div>
        </div>
      )}

      {/* Maintenance Overlay */}
      {dashboard.status === 'maintenance' && (
        <div className="maintenance-overlay">
          <div className="maintenance-notice">
            <span className="maintenance-icon">🔧</span>
            <span className="maintenance-text">Under Maintenance</span>
          </div>
        </div>
      )}

      {/* Development Overlay */}
      {dashboard.status === 'development' && (
        <div className="development-overlay">
          <div className="development-notice">
            <span className="development-icon">🔵</span>
            <span className="development-text">In Development</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardCard 